import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TaskModal from './TaskModal';
import '../../css/SubmissionReview.css';

const PRIORITY_BADGE = {
    NONE: null,
    LOW: { label: 'Low', className: 'low' },
    MEDIUM: { label: 'Medium', className: 'medium' },
    HIGH: { label: 'High', className: 'high' },
    URGENT: { label: 'Urgent', className: 'urgent' },
};

const SubmissionReview = ({ boardId, columns, boardAbbreviation, onClose, onReviewed }) => {
    const { apiCall } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const fetchSubmissions = useCallback(async () => {
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/submissions/?status=PENDING`);
            if (res.ok) {
                const data = await res.json();
                setSubmissions(data);
            }
        } catch (err) {
            console.error('Error fetching submissions:', err);
        }
        setLoading(false);
    }, [apiCall, boardId]);

    useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

    const handleApprove = async (submissionId, fields) => {
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/submissions/${submissionId}/approve/`, {
                method: 'POST',
                body: JSON.stringify(fields),
            });
            if (res.ok) {
                setSubmissions(prev => prev.filter(s => s.id !== submissionId));
                setSelected(null);
                if (onReviewed) onReviewed();
            } else {
                const data = await res.json().catch(() => ({}));
                window.alert(data.error || 'Failed to approve submission.');
            }
        } catch {
            window.alert('Failed to approve submission.');
        }
    };

    const handleDeny = async (submissionId, note) => {
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/submissions/${submissionId}/deny/`, {
                method: 'POST',
                body: JSON.stringify({ review_note: note }),
            });
            if (res.ok) {
                setSubmissions(prev => prev.filter(s => s.id !== submissionId));
                setSelected(null);
                if (onReviewed) onReviewed();
            } else {
                const data = await res.json().catch(() => ({}));
                window.alert(data.error || 'Failed to deny submission.');
            }
        } catch {
            window.alert('Failed to deny submission.');
        }
    };

    const reviewTask = selected && {
        id: selected.id,
        title: selected.title,
        description: selected.description || '',
        priority: selected.priority,
        due_date: selected.due_date,
        labels: selected.labels || [],
        task_type: selected.task_type || 'TASK',
        column: selected.suggested_column || (columns[0] && columns[0].id) || '',
        submitted_by_info: selected.submitted_by_info,
        suggested_column_name: selected.suggested_column_name,
        created_at: selected.created_at,
    };

    return (
        <>
            <div className="SubmissionReview__overlay" onClick={onClose}>
                <div className="SubmissionReview" onClick={(e) => e.stopPropagation()}>
                    <div className="SubmissionReview__header">
                        <h3 className="SubmissionReview__title">
                            Task Submissions
                            {submissions.length > 0 && (
                                <span className="SubmissionReview__count">{submissions.length}</span>
                            )}
                        </h3>
                        <button className="SubmissionReview__close" onClick={onClose}>×</button>
                    </div>

                    <div className="SubmissionReview__list">
                        {loading ? (
                            <div className="SubmissionReview__empty">Loading...</div>
                        ) : submissions.length === 0 ? (
                            <div className="SubmissionReview__empty">
                                <i className="fas fa-inbox" />
                                <p>No pending submissions to review.</p>
                            </div>
                        ) : (
                            submissions.map(sub => {
                                const priority = PRIORITY_BADGE[sub.priority];
                                return (
                                    <div
                                        key={sub.id}
                                        className="SubmissionReview__item"
                                        onClick={() => setSelected(sub)}
                                    >
                                        <div className="SubmissionReview__item-main">
                                            <h4 className="SubmissionReview__item-title">{sub.title}</h4>
                                            <div className="SubmissionReview__item-meta">
                                                <span className="SubmissionReview__item-author">
                                                    <i className="fas fa-user" />
                                                    {sub.submitted_by_info?.name || sub.submitted_by_info?.email || 'Visitor'}
                                                </span>
                                                {sub.suggested_column_name && (
                                                    <span className="SubmissionReview__item-column">
                                                        <i className="fas fa-columns" /> {sub.suggested_column_name}
                                                    </span>
                                                )}
                                                {priority && (
                                                    <span className={`TaskCard__priority TaskCard__priority--${priority.className}`}>
                                                        {priority.label}
                                                    </span>
                                                )}
                                                {(sub.labels || []).map((l, i) => (
                                                    <span key={i} className="TaskCard__label">{l}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <button className="SubmissionReview__item-review">
                                            Review <i className="fas fa-chevron-right" />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {reviewTask && (
                <TaskModal
                    mode="review"
                    task={reviewTask}
                    columns={columns}
                    boardAbbreviation={boardAbbreviation}
                    existingLabels={[]}
                    onClose={() => setSelected(null)}
                    onApprove={handleApprove}
                    onDeny={handleDeny}
                />
            )}
        </>
    );
};

export default SubmissionReview;
